import {
  AlertTriangle,
  Check,
  Cloud,
  CloudOff,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useSyncStatus } from '@/hooks/use-sync-status';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { useSyncContext } from '@/contexts/sync-context';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SyncStatus } from '@/types/sync';

interface ItemSyncBadgeProps {
  /** The sync status of this specific item. */
  status: SyncStatus;
  /** Optional className override. */
  className?: string;
}

/**
 * Per-item sync status badge.
 *
 * Shows the sync state for a single entity (job, inspection, etc.)
 * with appropriate colour coding and icon.
 */
export function ItemSyncBadge({ status, className }: ItemSyncBadgeProps) {
  const config = {
    pending: {
      label: 'Saved locally',
      icon: CloudOff,
      variant: 'outline' as const,
      className: 'text-muted-foreground border-muted-foreground/30',
    },
    syncing: {
      label: 'Syncing...',
      icon: Loader2,
      variant: 'outline' as const,
      className: 'text-blue-600 border-blue-300 animate-pulse',
    },
    synced: {
      label: 'Synced',
      icon: Check,
      variant: 'outline' as const,
      className: 'text-green-600 border-green-300',
    },
    failed: {
      label: 'Sync failed',
      icon: AlertTriangle,
      variant: 'destructive' as const,
      className: '',
    },
  };

  const c = config[status];
  const Icon = c.icon;

  return (
    <Badge variant={c.variant} className={cn('gap-1 text-[10px]', c.className, className)}>
      <Icon className={cn('h-3 w-3', status === 'syncing' && 'animate-spin')} />
      {c.label}
    </Badge>
  );
}

/**
 * Global sync status badge for the header.
 *
 * Shows:
 * - Green cloud + "Synced" when all queues are empty and online
 * - Blue spinning loader + count when syncing
 * - Orange warning + count when items are pending (offline)
 * - Red alert + retry button when items have failed
 */
export function SyncStatusBadge() {
  const { isOnline } = useOnlineStatus();
  const { totalPending, totalFailed } = useSyncStatus();
  const { status, retryFailed } = useSyncContext();

  // Offline with pending items
  if (!isOnline) {
    return (
      <Badge
        variant="outline"
        className="gap-1.5 border-amber-300 bg-amber-50 text-amber-700 text-xs"
      >
        <CloudOff className="h-3.5 w-3.5" />
        <span>Offline</span>
        {totalPending > 0 && (
          <span className="ml-0.5 font-bold">{totalPending}</span>
        )}
      </Badge>
    );
  }

  // Failed items
  if (totalFailed > 0) {
    return (
      <div className="flex items-center gap-1.5">
        <Badge
          variant="destructive"
          className="gap-1 text-xs"
        >
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>{totalFailed} failed</span>
        </Badge>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={retryFailed}
          title="Retry failed items"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  // Syncing
  if (status.isSyncing || totalPending > 0) {
    return (
      <Badge
        variant="outline"
        className="gap-1.5 border-blue-300 bg-blue-50 text-blue-700 text-xs"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Syncing {totalPending > 0 ? totalPending : ''}</span>
      </Badge>
    );
  }

  // All synced
  return (
    <Badge
      variant="outline"
      className="gap-1.5 border-green-300 bg-green-50 text-green-700 text-xs"
    >
      <Cloud className="h-3.5 w-3.5" />
      <span>Synced</span>
    </Badge>
  );
}
